import { useEffect, useState, type CSSProperties } from 'react'
import type { Order, Shipment } from '../api/types'
import { useDatumFormat, useSprache, useT, type Textschluessel } from '../i18n'
import { useMeldung } from './Meldung'

/**
 * Der Stand einer Bestellung als Weg, nicht als Wort.
 *
 * Vorher stand dort eine Zeile: „Stand: Versandt". Das ist richtig und sagt
 * trotzdem nicht, was ein Kunde wissen will — nämlich wo im Ablauf er steht
 * und was noch kommt. Jeder kennt die Anzeige vom Paketdienst, und genau die
 * Erwartung bringt er mit, wenn er eine Bestellnummer eingibt.
 *
 * Die Stufen unterscheiden sich zwischen Versand und Abholung, und zwar in
 * den letzten beiden: „Versandt → Zugestellt" ist bei einer Abholung schlicht
 * falsch, dort heißt es „Abholbereit → Abgeholt". Alles davor ist dasselbe.
 */

/** Wie weit eine Bestellung ist. Der Index ist die erreichte Stufe. */
const STUFE: Record<string, number> = {
  draft: 0,
  pending: 0,
  open: 0,
  awaiting_payment: 0,
  confirmed: 0,
  paid: 1,
  processing: 2,
  packed: 2,
  shipped: 3,
  ready: 3,
  ready_for_pickup: 3,
  delivered: 4,
  completed: 4,
  picked_up: 4,
}

/**
 * Zustände, die keinen Weg mehr haben.
 *
 * Für sie gibt es keine Fortschrittsanzeige: Ein Balken, der bei einer
 * stornierten Bestellung zu drei Fünfteln in Akzentfarbe steht, behauptet,
 * dass es weitergeht.
 */
const ABGEBROCHEN = new Set(['cancelled', 'canceled', 'refunded'])

/** Die Beschriftungen aller Stände, die der Server kennt. */
const STANDTEXT: Record<string, Textschluessel> = {
  pending: 'stand.pending',
  open: 'stand.pending',
  confirmed: 'stand.confirmed',
  awaiting_payment: 'stand.awaiting_payment',
  draft: 'stand.draft',
  paid: 'stand.paid',
  processing: 'stand.processing',
  packed: 'stand.packed',
  ready: 'stand.ready',
  ready_for_pickup: 'stand.ready',
  shipped: 'stand.shipped',
  delivered: 'stand.delivered',
  completed: 'stand.completed',
  picked_up: 'stand.picked_up',
  cancelled: 'stand.cancelled',
  canceled: 'stand.cancelled',
  refunded: 'stand.refunded',
}

/**
 * Der Stand in Worten.
 *
 * Ein unbekannter Status wird gezeigt, nicht verschluckt: Lieber ein
 * englisches Wort aus der Datenbank als ein leeres Feld, das nach einem Fehler
 * aussieht.
 */
export function useStatustext(): (status: string) => string {
  const { t } = useSprache()
  return (status) => {
    const schluessel = STANDTEXT[status]
    return schluessel ? t(schluessel) : status
  }
}

/** Ob dieser Stand ein Abbruch ist — dann gilt kein Weg mehr. */
export function istAbgebrochen(status: string): boolean {
  return ABGEBROCHEN.has(status)
}

/**
 * Die Marke mit dem Stand — dieselbe in der Verfolgung wie in der Liste.
 *
 * Die Farbe allein sagt nichts: Sie trägt das Wort, nicht umgekehrt. Wer
 * Farben nicht unterscheidet, liest denselben Zustand.
 */
export function Standmarke({ status }: { status: string }) {
  const statustext = useStatustext()
  const art = istAbgebrochen(status)
    ? 'abgebrochen'
    : (STUFE[status] ?? 0) >= 4
      ? 'fertig'
      : 'laeuft'

  return (
    <span className="standmarke" data-art={art}>
      {statustext(status)}
    </span>
  )
}

/**
 * Kopieren in die Zwischenablage, mit Rückmeldung, die von selbst verschwindet.
 *
 * Als Haken und nicht als Knopf: Die Sendungsnummer steht in einem Feld mit
 * beschriftetem Knopf daneben, die Bestellnummer in der Kopfleiste braucht nur
 * ein Symbol. Zwei Knöpfe mit derselben Logik wären eine Logik zu viel — der
 * seltener angefasste veraltet.
 *
 * Bliebe „Kopiert" stehen, wüsste beim zweiten Klick niemand, ob etwas
 * passiert ist.
 */
export function useKopieren(wert: string): { kopiert: boolean; kopieren: () => void } {
  const [kopiert, setKopiert] = useState(false)

  useEffect(() => {
    if (!kopiert) return
    const uhr = setTimeout(() => setKopiert(false), 2000)
    return () => clearTimeout(uhr)
  }, [kopiert])

  const melden = useMeldung()
  const t = useT()

  const kopieren = () => {
    // Ohne sichere Herkunft oder ohne Erlaubnis gibt es keine Zwischenablage.
    // Dann bleibt der Wert stehen und lässt sich von Hand markieren — das ist
    // der Grund, aus dem er in einem eigenen, auswählbaren Feld steht und
    // nicht als Beschriftung im Knopf.
    navigator.clipboard?.writeText(wert).then(
      () => {
        setKopiert(true)
        // Und sichtbar, nicht nur als Symbolwechsel am Knopf: Wer kopiert,
        // schaut im selben Augenblick woanders hin — zum Feld, in das er
        // einfügen will. Ein Häkchen an der Stelle, die er gerade verlässt,
        // sieht er nie.
        melden(t('bestellung.nummerKopiert'))
      },
      () => {
        // Und wenn nicht, dann sagen wir das auch.
        //
        // Vorher verschluckte dieser Zweig den Fehlschlag: Auf einer unsicheren
        // Verbindung oder mit verweigerter Erlaubnis tat der Knopf sichtbar
        // nichts, und der Kunde drückte ihn noch dreimal. Der Wert steht in
        // einem auswählbaren Feld daneben — darauf weist die Meldung hin,
        // statt ihn ratlos zu lassen.
        melden(t('bestellung.nummerNichtKopiert'))
      },
    )
  }

  return { kopiert, kopieren }
}

export function Verfolgung({ bestellung }: { bestellung: Order }) {
  const { t } = useSprache()
  const datum = useDatumFormat()

  const abholung = bestellung.fulfilment === 'pickup'
  const sendungen = bestellung.shipments ?? []
  // Der Zeitpunkt der Zustellung steht an der Bestellung *und* an der Sendung.
  // Die Bestellung zuerst: Eine Abholung hat keine Sendung, und ohne dieses
  // Feld bliebe die letzte Stufe dort für immer ohne Zeitpunkt.
  const zugestelltAm =
    bestellung.deliveredAt ?? sendungen.find((s) => s.deliveredAt)?.deliveredAt ?? null

  // Der eigene Platz in dieser Liste, damit das Ausblenden weiter unten die
  // Zählung nicht verschiebt: Wer Stufe 3 ist, bleibt Stufe 3, auch wenn vor
  // ihm einer weggefallen ist.
  const stufen: {
    stand: number
    name: string
    zeit: string | null | undefined
    /** Diese Stufe hat von Haus aus keinen Zeitpunkt — siehe unten. */
    ohneZeit?: boolean
  }[] = [
    { stand: 0, name: t('verfolgung.eingegangen'), zeit: bestellung.createdAt },
    { stand: 1, name: t('verfolgung.bezahlt'), zeit: bestellung.paidAt },
    // Ohne Zeitpunkt, und das mit Absicht: Der Server meldet nicht, wann das
    // Packen begann. Einen zu erfinden — etwa den der Zahlung — hieße, eine
    // Uhrzeit zu behaupten, die niemand kennt.
    { stand: 2, name: t('verfolgung.inBearbeitung'), zeit: null, ohneZeit: true },
    {
      stand: 3,
      name: abholung ? t('verfolgung.abholbereit') : t('verfolgung.versandt'),
      zeit: bestellung.shippedAt ?? sendungen.find((s) => s.shippedAt)?.shippedAt,
    },
    {
      stand: 4,
      name: abholung ? t('verfolgung.abgeholt') : t('verfolgung.zugestellt'),
      zeit: zugestelltAm,
    },
  ]

  if (istAbgebrochen(bestellung.status)) {
    return (
      <p className="hinweis hinweis-fehler" role="status">
        {t('verfolgung.abgebrochen')}
      </p>
    )
  }

  // Einen unbekannten Stand nicht auf Stufe 0 raten: Die Marke daneben sagt
  // dann, was der Server meldet, und das ist ehrlicher als ein Balken, der
  // behauptet, die Bestellung sei gerade erst eingegangen.
  const stand = STUFE[bestellung.status]
  if (stand === undefined) return null

  // „In Bearbeitung" sagt, wo die Bestellung *gerade* ist — als Vergangenheit
  // hat die Stufe keinen Wert. Sie ist die einzige, die von Haus aus keinen
  // Zeitpunkt hat, und sobald etwas unterwegs ist, steht sie als einzige ohne
  // Datum zwischen lauter datierten und sieht aus wie ein Ladefehler. Also:
  // zeigen, solange sie bevorsteht oder gerade dran ist, danach weglassen.
  //
  // Ausdrücklich nur diese eine Stufe, nicht jede ohne Datum: Fehlt bei einer
  // zugestellten Bestellung der Versandzeitpunkt, ist das eine Lücke in den
  // Daten — dann soll „Versandt" trotzdem stehen bleiben, denn versandt wurde
  // sie ja. Eine Stufe verschwinden zu lassen, weil ein Feld leer blieb,
  // verschweigt den Schritt statt die Lücke.
  const gezeigt = stufen.filter((stufe) => !stufe.ohneZeit || stufe.stand >= stand)

  return (
    <ol
      className="verfolgung"
      aria-label={t('verfolgung.aria')}
      style={{ '--stufen': gezeigt.length } as CSSProperties}
    >
      {gezeigt.map((stufe) => {
        const erreicht = stufe.stand <= stand
        return (
          <li
            key={stufe.name}
            className="verfolgung-schritt"
            data-erreicht={erreicht ? 'ja' : 'nein'}
            aria-current={stufe.stand === stand ? 'step' : undefined}
          >
            <span className="verfolgung-punkt" aria-hidden="true" />
            <span className="verfolgung-inhalt">
              <span className="verfolgung-name">{stufe.name}</span>
              {/* Die Uhrzeit gehört dazu: „12.09.2026" allein lässt offen, ob
                  das Paket heute früh oder vor einer Woche losging. */}
              {erreicht && stufe.zeit && datum(stufe.zeit, true) && (
                <time className="verfolgung-zeit" dateTime={stufe.zeit}>
                  {datum(stufe.zeit, true)}
                </time>
              )}
              {/* Blass gegenüber kräftig ist für eine Sprachausgabe kein
                  Unterschied — deshalb steht er hier auch in Worten. */}
              <span className="nur-fuer-screenreader">
                {erreicht ? t('verfolgung.erledigt') : t('verfolgung.offen')}
              </span>
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Eine Sendung: Paketdienst, Nummer, Verweis.
 *
 * Mehrere je Bestellung sind der Normalfall, sobald etwas nachgeliefert wird.
 * Die Sendungsnummer steht groß und zum Kopieren da — sie wird abgetippt, in
 * ein anderes Feld eingefügt oder am Telefon vorgelesen, und dafür taugt eine
 * Zeile Fließtext nicht.
 *
 * Der Verfolgungsverweis kommt fertig vom Server und wird hier nie selbst
 * gebaut: Dort steht, welche Dienste es gibt und wie ihre Adressen aussehen.
 */
export function Sendungskarte({
  sendung,
  nummer,
  anzahl,
}: {
  sendung: Shipment
  nummer: number
  anzahl: number
}) {
  const { t } = useSprache()
  const datum = useDatumFormat()
  const { kopiert, kopieren } = useKopieren(sendung.trackingCode)

  const versandt = datum(sendung.shippedAt)
  const zugestellt = datum(sendung.deliveredAt)

  return (
    <section className="sendungskarte">
      <header className="sendungskarte-kopf">
        {/* Ein Paketsymbol statt einer Symbolschrift — aus demselben Grund wie
            der Einkaufswagen in der Kopfzeile. */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3 20.5 7.5v9L12 21l-8.5-4.5v-9L12 3Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M3.5 7.5 12 12m0 0 8.5-4.5M12 12v9" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <span className="sendungskarte-titel">
          {anzahl > 1 ? t('sendung.titelMehrere', { nummer, anzahl }) : t('sendung.titel')}
        </span>
      </header>

      <p className="sendungskarte-dienst">
        <span className="feld-hinweis">{t('sendung.paketdienst')}</span>
        {/* Der Name des Dienstes kommt vom Server — „DHL" heißt in jeder
            Sprache DHL. */}
        <strong>{sendung.carrierName || sendung.carrier || t('sendung.derPaketdienst')}</strong>
      </p>

      {sendung.trackingCode && (
        <p className="sendungsnummer">
          <span className="feld-hinweis">{t('sendung.nummer')}</span>
          <span className="sendungsnummer-zeile">
            <code>{sendung.trackingCode}</code>
            <button className="knopf knopf-schlicht" type="button" onClick={kopieren}>
              {kopiert ? t('sendung.kopiert') : t('sendung.kopieren')}
            </button>
          </span>
          <span className="nur-fuer-screenreader" role="status">
            {kopiert ? t('sendung.kopiert') : ''}
          </span>
        </p>
      )}

      {(versandt || zugestellt) && (
        <p className="feld-hinweis sendungskarte-zeiten">
          {versandt && t('sendung.versandtAm', { datum: versandt })}
          {versandt && zugestellt && ' · '}
          {zugestellt && t('sendung.zugestelltAm', { datum: zugestellt })}
        </p>
      )}

      {sendung.trackingUrl && (
        <p className="sendungskarte-knopf">
          {/* In einem neuen Fenster: Die Seite des Paketdienstes ist eine
              fremde, und wer von dort zurückwill, hat sonst nur den
              Zurückknopf — und der bringt ihn auf ein Suchformular, das er neu
              ausfüllen müsste. */}
          <a
            className="knopf"
            href={sendung.trackingUrl}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={t('sendung.verfolgenNeuesFenster')}
          >
            {t('sendung.verfolgen')}
          </a>
        </p>
      )}
    </section>
  )
}
